import { useCallback } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn, useTranslation } from "@/packages/utils";
import { springs } from "@/components/ui/motion";
import type { OrgContextAnswers } from "@/components/audits/NewAuditWizard/types";

export type { OrgContextAnswers };

export function isCompleteOrgContext(
  answers: Partial<OrgContextAnswers>,
): answers is OrgContextAnswers {
  return (
    answers.physicalPremises !== undefined &&
    answers.infrastructure !== undefined &&
    answers.physicalMedia !== undefined &&
    answers.secureAreas !== undefined &&
    answers.workforceModel !== undefined
  );
}

interface OrgContextFormProps {
  answers: Partial<OrgContextAnswers>;
  onChange: (answers: Partial<OrgContextAnswers>) => void;
  disabled?: boolean;
  compact?: boolean;
}

const PHYSICAL_PREMISES_OPTIONS = ["yes", "no", "partial"] as const;
const INFRASTRUCTURE_OPTIONS = [
  "cloud-only",
  "own-managed",
  "colocated",
] as const;
const BOOLEAN_OPTIONS = ["true", "false"] as const;
const WORKFORCE_MODEL_OPTIONS = ["fully-remote", "hybrid", "on-site"] as const;

export function OrgContextForm({
  answers,
  onChange,
  disabled,
  compact = false,
}: OrgContextFormProps) {
  const { t } = useTranslation("audits");

  const handleAnswer = useCallback(
    <K extends keyof OrgContextAnswers>(
      key: K,
      value: OrgContextAnswers[K],
    ) => {
      onChange({ ...answers, [key]: value });
    },
    [answers, onChange],
  );

  return (
    <div className={compact ? "flex flex-col gap-4" : "flex flex-col gap-6"}>
      <QuestionGroup
        label={t("wizard.orgContext.physicalPremises.label")}
        options={PHYSICAL_PREMISES_OPTIONS.map((opt) => ({
          value: opt,
          label: t(`wizard.orgContext.physicalPremises.options.${opt}`),
        }))}
        selected={answers.physicalPremises}
        onSelect={(val) => handleAnswer("physicalPremises", val)}
        disabled={disabled}
        compact={compact}
      />

      <QuestionGroup
        label={t("wizard.orgContext.infrastructure.label")}
        options={INFRASTRUCTURE_OPTIONS.map((opt) => ({
          value: opt,
          label: t(`wizard.orgContext.infrastructure.options.${opt}`),
        }))}
        selected={answers.infrastructure}
        onSelect={(val) => handleAnswer("infrastructure", val)}
        disabled={disabled}
        compact={compact}
      />

      <QuestionGroup
        label={t("wizard.orgContext.physicalMedia.label")}
        options={BOOLEAN_OPTIONS.map((opt) => ({
          value: opt,
          label: t(`wizard.orgContext.physicalMedia.options.${opt}`),
        }))}
        selected={
          answers.physicalMedia !== undefined
            ? String(answers.physicalMedia)
            : undefined
        }
        onSelect={(val) => handleAnswer("physicalMedia", val === "true")}
        disabled={disabled}
        compact={compact}
      />

      <QuestionGroup
        label={t("wizard.orgContext.secureAreas.label")}
        options={BOOLEAN_OPTIONS.map((opt) => ({
          value: opt,
          label: t(`wizard.orgContext.secureAreas.options.${opt}`),
        }))}
        selected={
          answers.secureAreas !== undefined
            ? String(answers.secureAreas)
            : undefined
        }
        onSelect={(val) => handleAnswer("secureAreas", val === "true")}
        disabled={disabled}
        compact={compact}
      />

      <QuestionGroup
        label={t("wizard.orgContext.workforceModel.label")}
        options={WORKFORCE_MODEL_OPTIONS.map((opt) => ({
          value: opt,
          label: t(`wizard.orgContext.workforceModel.options.${opt}`),
        }))}
        selected={answers.workforceModel}
        onSelect={(val) => handleAnswer("workforceModel", val)}
        disabled={disabled}
        compact={compact}
      />
    </div>
  );
}

interface QuestionGroupProps<T extends string> {
  label: string;
  options: { value: T; label: string }[];
  selected: T | undefined;
  onSelect: (value: T) => void;
  disabled?: boolean;
  compact?: boolean;
}

function QuestionGroup<T extends string>({
  label,
  options,
  selected,
  onSelect,
  disabled,
  compact = false,
}: QuestionGroupProps<T>) {
  return (
    <div className={compact ? "flex flex-col gap-1.5" : "flex flex-col gap-2"}>
      <span
        className={
          compact
            ? "text-[12px] font-medium text-primary"
            : "text-[12px] font-medium text-primary"
        }
      >
        {label}
      </span>
      <div className={compact ? "grid gap-1.5 sm:grid-cols-2" : "grid gap-1.5"}>
        {options.map((opt) => {
          const isSelected = selected === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => !disabled && onSelect(opt.value)}
              disabled={disabled}
              className={cn(
                compact
                  ? "flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-[12px] transition-all duration-150"
                  : "flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-[12px] transition-all duration-150",
                isSelected
                  ? "border-accent bg-accent/5 text-accent font-medium"
                  : "border-border hover:border-accent/40 text-primary",
                disabled && "opacity-50 cursor-not-allowed",
              )}
            >
              <div
                className={cn(
                  compact
                    ? "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 transition-colors"
                    : "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                  isSelected ? "border-accent bg-accent" : "border-border",
                )}
              >
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={springs.bouncy}
                  >
                    <Check
                      className={
                        compact
                          ? "h-3 w-3 text-white"
                          : "h-2.5 w-2.5 text-white"
                      }
                    />
                  </motion.div>
                )}
              </div>
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
